/**
 * Sound effects. There are no audio files: every sound is generated
 * ("synthesized") while the game runs, using the browser's Web Audio API.
 *
 * The work is split in two:
 * - `SoundEvents` decides which sounds to play, by reading new entries in the
 *   game's event log. It's plain logic, so tests can check it with a fake
 *   synth.
 * - `WebSynth` actually makes the sounds.
 *
 * Splitting "decide" from "do" like this is a common way to make code that
 * talks to the outside world (speakers, networks, files) testable.
 */
import type { GameState } from "./state";

/** Every sound the game can make. */
export type SoundName =
	| "launch"
	| "blocked"
	| "clear"
	| "stow"
	| "retire"
	| "won"
	| "lost";

/**
 * Anything that can play a named sound. `WebSynth` is the real one; tests
 * pass in a fake that just records what was asked for. Code that depends on
 * this interface, rather than on WebSynth itself, doesn't care which it gets.
 */
export interface Synth {
	play(name: SoundName, step?: number): void;
}

/** Sound settings. */
export interface SoundSettings {
	/** Master volume, from 0 (silent) to 100. */
	volume: number;
	/**
	 * Cells cleared closer together than this many milliseconds count as a
	 * "combo", and each one plays a higher note.
	 */
	comboMs: number;
	/**
	 * The most clear sounds started in one frame. Clearing many cells at once
	 * would otherwise stack up into a loud, distorted burst.
	 */
	maxClearsPerFrame: number;
}

/** The default sound settings. */
export const DEFAULT_SOUND_SETTINGS: SoundSettings = {
	volume: 60,
	comboMs: 450,
	maxClearsPerFrame: 3,
};

/** Plays the right sound for each new entry in the game's event log. */
export class SoundEvents {
	private state: GameState | null = null;
	private seen = 0;
	private combo = 0;
	private lastClear = Number.NEGATIVE_INFINITY;

	constructor(
		private readonly synth: Synth,
		public settings: SoundSettings = { ...DEFAULT_SOUND_SETTINGS },
	) {}

	/** Play sounds for any events logged since the last call. Call once per frame. */
	sync(state: GameState, now: number): void {
		if (state !== this.state) {
			this.state = state;
			this.seen = 0;
			this.combo = 0;
		}
		let clears = 0;
		for (const event of state.events.slice(this.seen)) {
			switch (event.type) {
				case "clear":
					this.combo =
						now - this.lastClear < this.settings.comboMs ? this.combo + 1 : 0;
					this.lastClear = now;
					if (clears++ < this.settings.maxClearsPerFrame)
						this.synth.play("clear", this.combo);
					break;
				case "end":
					this.synth.play(event.status);
					break;
				case "autoplay":
					break; // the launches that follow make their own sounds
				default:
					this.synth.play(event.type);
			}
		}
		this.seen = state.events.length;
	}
}

/**
 * The notes of a "major pentatonic" scale, as steps up from the starting note
 * (12 steps make an octave). Pentatonic scales sound pleasant whatever order
 * their notes are played in, which suits sounds triggered by gameplay.
 */
const PENTATONIC = [0, 2, 4, 7, 9];

/**
 * The frequency (in Hz) of the `step`th note of the scale, starting at
 * `base`. Each step past the end of the scale goes up an octave, which
 * doubles the frequency.
 */
function noteHz(base: number, step: number): number {
	const octave = Math.floor(step / PENTATONIC.length);
	const semitones = (PENTATONIC[step % PENTATONIC.length] ?? 0) + 12 * octave;
	return base * 2 ** (semitones / 12);
}

/**
 * Makes the sounds, using the browser's Web Audio API.
 *
 * Browsers don't let pages play sound until the user has interacted with
 * them (to stop pages making noise on their own). So the audio system is
 * only set up in `unlock()`, which main.ts calls on the first click.
 */
export class WebSynth implements Synth {
	private ctx: AudioContext | null = null;
	private master: GainNode | null = null;
	private volume = DEFAULT_SOUND_SETTINGS.volume;
	private muted = false;

	/**
	 * Set up the audio system, or wake it if the browser paused it. Must be
	 * called while handling a user action, such as a click.
	 */
	unlock(): void {
		if (!this.ctx) {
			if (typeof AudioContext === "undefined") return;
			this.ctx = new AudioContext();
			this.master = this.ctx.createGain();
			this.master.connect(this.ctx.destination);
			this.applyGain();
		}
		if (this.ctx.state === "suspended") void this.ctx.resume();
	}

	/** Set the master volume, from 0 to 100. */
	setVolume(volume: number): void {
		this.volume = volume;
		this.applyGain();
	}

	/** Mute or unmute, keeping the volume setting. */
	setMuted(muted: boolean): void {
		this.muted = muted;
		this.applyGain();
	}

	/** Play a sound now. For clear sounds, `step` picks a higher note in the scale, for combos. */
	play(name: SoundName, step = 0): void {
		if (!this.ctx || this.muted || this.volume === 0) return;
		switch (name) {
			case "launch":
				this.tone({
					type: "triangle",
					from: 330,
					to: 660,
					duration: 0.09,
					gain: 0.35,
				});
				break;
			case "blocked":
				this.tone({
					type: "square",
					from: 110,
					to: 98,
					duration: 0.12,
					gain: 0.12,
				});
				break;
			case "clear": {
				// Stop rising after two octaves, so long combos don't get shrill.
				const hz = noteHz(523.25, Math.min(step, PENTATONIC.length * 2));
				this.tone({
					type: "triangle",
					from: hz,
					to: hz,
					duration: 0.14,
					gain: 0.3,
				});
				break;
			}
			case "stow":
				this.tone({
					type: "sine",
					from: 240,
					to: 160,
					duration: 0.14,
					gain: 0.45,
				});
				break;
			case "retire":
				this.tone({
					type: "sine",
					from: 660,
					to: 660,
					duration: 0.12,
					gain: 0.3,
				});
				this.tone({
					type: "sine",
					from: 990,
					to: 990,
					duration: 0.18,
					gain: 0.3,
					delay: 0.08,
				});
				break;
			case "won":
				[523.25, 659.25, 783.99, 1046.5].forEach((hz, i) => {
					this.tone({
						type: "triangle",
						from: hz,
						to: hz,
						duration: 0.22,
						gain: 0.35,
						delay: i * 0.11,
					});
				});
				break;
			case "lost":
				[392, 329.63, 261.63].forEach((hz, i) => {
					this.tone({
						type: "sine",
						from: hz,
						to: hz * 0.97,
						duration: 0.3,
						gain: 0.4,
						delay: i * 0.18,
					});
				});
				break;
		}
	}

	/**
	 * Update the master volume. Changes are smoothed over a few milliseconds,
	 * because jumping instantly causes an audible click. The volume is squared
	 * because hearing isn't linear: this makes the slider feel more even.
	 */
	private applyGain(): void {
		if (!this.ctx || !this.master) return;
		const level = this.muted ? 0 : (this.volume / 100) ** 2; // squared, so the volume slider feels even to the ear
		this.master.gain.setTargetAtTime(level, this.ctx.currentTime, 0.01);
	}

	/**
	 * Play one note. An "oscillator" generates the tone (optionally sliding in
	 * pitch), and an "envelope" shapes its volume over time: a quick rise, then
	 * a fade to silence.
	 */
	private tone({
		type,
		from,
		to,
		duration,
		gain,
		delay = 0,
	}: {
		type: OscillatorType;
		from: number;
		to: number;
		duration: number;
		gain: number;
		delay?: number;
	}): void {
		const { ctx, master } = this;
		if (!ctx || !master) return;
		const start = ctx.currentTime + delay;
		const osc = ctx.createOscillator();
		const env = ctx.createGain();
		osc.type = type;
		osc.frequency.setValueAtTime(from, start);
		if (to !== from)
			osc.frequency.exponentialRampToValueAtTime(to, start + duration);
		// Rise quickly from near silence, then fade out. Starting and ending at (near)
		// zero volume avoids clicks.
		env.gain.setValueAtTime(0.0001, start);
		env.gain.exponentialRampToValueAtTime(gain, start + 0.008);
		env.gain.exponentialRampToValueAtTime(0.0001, start + duration);
		osc.connect(env).connect(master);
		osc.start(start);
		osc.stop(start + duration + 0.02);
	}
}
